import React, { useEffect, useState } from "react";

import { LoadingIcon as Loading, Modal, Typography } from "@packages/ds";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

import ContentLayout from "Components/ContentLayout";
import Button, { BackLink, ButtonWrapper } from "ds/Button";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import useUrls from "Hooks/useUrls";
import {
  deleteIntegration,
  getIntegration,
  updateIntegration
} from "Reducers/integration";
import { gitProjectSelector } from "Reducers/project/project";
import { useAppDispatch, useAppSelector } from "Store/hooks";

import IntegrationForm from "../components/IntegrationForm/Pages";

import * as S from "./Configure.styles";

import type { APIObject } from "@packages/client";

type Props = {
  integrationId: string;
  organizationId: string;
  projectId: string;
};

const ConfigureIntegration = ({
  integrationId,
  organizationId,
  projectId
}: Props) => {
  const [showModal, toggleModal] = useState(false);

  const project = useSelectorWithUrlParams(gitProjectSelector);
  const integration = useAppSelector(
    state => state.integration.data[projectId]?.[integrationId]
  );
  const status = useAppSelector(state => state.integration.status);

  const dispatch = useAppDispatch();
  const intl = useIntl();
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(getIntegration({ projectId, integrationId }));
  }, [dispatch, integrationId, projectId]);

  const [integrationsUrl, integrationUrl] = useUrls(
    [
      "organization.project.settings.integrations",
      "organization.project.settings.integrations.integration"
    ],
    {
      organizationId,
      projectId,
      integrationId: integrationId ?? ""
    }
  );

  useEffect(() => {
    if (status === "updated") {
      navigate(integrationUrl);
    }

    if (status === "deleted") {
      navigate(integrationsUrl, { replace: true });
    }
  }, [navigate, integrationId, integrationsUrl, integrationUrl, status]);

  if (!integration) return null;

  const onCancel = () => {
    navigate(integrationUrl);
  };

  const onSubmit = (data: APIObject) => {
    dispatch(updateIntegration({ projectId, integration, data }));
  };

  const onConfirmRemove = (e: React.MouseEvent) => {
    if (e) e.preventDefault();
    dispatch(deleteIntegration({ projectId, integration }));
  };

  return (
    <ContentLayout>
      <BackLink
        to={integrationUrl}
        from={integration?.type}
        capitalized={false}
      />
      <S.Title tag="h2">
        {intl.formatMessage({
          id: "integration.configure.title",
          defaultMessage: "Edit your integration"
        })}
      </S.Title>

      {integration?.hasLink("#edit") && (
        <S.Content>
          <Modal
            isOpen={showModal}
            onOpenChange={() => {
              toggleModal(false);
            }}
            handleClose={() => {
              toggleModal(false);
            }}
          >
            <Typography.Title tag="h2">
              {intl.formatMessage({
                id: "integration.remove.title",
                defaultMessage: "Remove integration"
              })}
            </Typography.Title>
            <Typography.BodyText>
              {intl.formatMessage(
                {
                  id: "integration.remove.sentence"
                },
                {
                  b: chunks => <b>{chunks}</b>,
                  integration: intl.formatMessage({
                    id: `integration.type.${integration.type}`
                  }),
                  project: project?.title
                }
              )}
            </Typography.BodyText>
            <ButtonWrapper spacing="modal" justifyContent="end">
              <Button
                analyticId="cancel"
                disabled={status === "pending"}
                variant="secondary"
                onClick={() => {
                  toggleModal(false);
                }}
              >
                {intl.formatMessage({ id: "cancel" })}
              </Button>
              {status !== "pending" && (
                <Button
                  analyticId="confirm_remove"
                  id="delete-integration-confirm-modal-button"
                  onClick={onConfirmRemove}
                >
                  {intl.formatMessage({
                    id: "confirm_remove",
                    defaultMessage: "Yes"
                  })}
                </Button>
              )}
              {status === "pending" && <Loading />}
            </ButtonWrapper>
          </Modal>

          <IntegrationForm
            integration={integration}
            onSubmit={onSubmit}
            onCancel={onCancel}
            onRemove={() => {
              toggleModal(true);
            }}
          />
        </S.Content>
      )}
    </ContentLayout>
  );
};

export default ConfigureIntegration;
